/**
 * Aufträge an das Modell (S45): reading an old menu (S41), translating (S36) and checking the German
 * (S47) wait in one queue,
 * the app starts a job, asks for it every few seconds and collects what came out. What the app sees
 * of a job; the queue itself (tables, runner, recovery) is the server's.
 */
import type { ReadFile, Reading, SkippedPage } from "./menu-reading.ts";
import type { Lang } from "./translation.ts";

export const AI_JOB_KINDS = ["read", "translate", "proofread"] as const;
export type AiJobKind = (typeof AI_JOB_KINDS)[number];
export type AiJobStatus = "queued" | "running" | "done" | "failed";

/** What a job of each kind gives when done. */
export interface AiJobResults {
  /** `files`: what was read, in the order the model counts them (S50: the pages it left out name a file). */
  /** `unread`: pages of a PDF the model did not get to, even when asked once more (S41). */
  read: { reading: Reading; from: string; files?: ReadFile[]; unread?: SkippedPage[]; cost: number | null };
  /** `lang` the first of `langs` (an older app reads only it); `byLang` how many per language (S61). */
  /** `left`: how many the model's answers left out even when asked again (03.11.2026); they stay open. */
  translate: { lang: Lang; langs?: Lang[]; byLang?: Partial<Record<Lang, number>>; translated: number; left?: number; cost: number | null };
  /** How many texts were checked, how many corrections they got; `left` as for translating. */
  proofread: { checked: number; suggestions: number; left?: number; cost: number | null };
}

export interface AiJob<K extends AiJobKind = AiJobKind> {
  id: string;
  kind: K;
  status: AiJobStatus;
  /** How many jobs wait before it; 0 when it is next or running. */
  ahead: number;
  result: AiJobResults[K] | null;
  error: { code: string; message: string; status: number } | null;
  createdAt: number;
  startedAt: number | null;
  finishedAt: number | null;
}

export const AI_JOB_LIMITS = {
  /** Jobs of all businesses waiting at once; more: „gerade ausgelastet“. */
  waiting: 20,
  /** A job cut off by a restart is read once more; the second time it is given up. */
  attempts: 2,
  /** A job nobody collected goes after a week. */
  keepMs: 7 * 86_400_000,
} as const;

export const isAiJobKind = (raw: unknown): raw is AiJobKind => (AI_JOB_KINDS as readonly unknown[]).includes(raw);

/** Whether the job is still to come: waiting or being read. */
export const aiJobOpen = (job: Pick<AiJob, "status">) => job.status === "queued" || job.status === "running";
